import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { WindowService } from 'src/shared/providers/window.service';

import { TranslationService } from '../../../providers/translation.service';

import { ValueAccessorBase } from '../default-class/value-accessor';

import { SelectModel } from './select.model';

@Component({
    selector: 'tidy-select',
    encapsulation: ViewEncapsulation.None,
    templateUrl: 'select.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: SelectComponent,
            multi: true,
        },
    ],
    styleUrls: ['./select.component.scss'],
    standalone: false
})
export class SelectComponent extends ValueAccessorBase<any> implements OnInit, OnChanges {

  @Input() disableElement: boolean;
  @Input() items: SelectModel[] = [];
  @Input() listedItems: SelectModel[] = [];
  @Input() label: string;
  @Input() helperText: string;
  @Input() icon: string;
  @Input() form: FormControl = new FormControl();
  @Input() submitted: boolean;
  @Input() smallText: boolean;
  @Input() hideSearch: boolean;
  @Input() searchPlaceholder = '';
  @Input() public errorMessage: string;
  @Input() multiple = false;
  @Output() optionChange: EventEmitter<string> = new EventEmitter<string>();

  @Input() selectedItem: any;
  @ViewChild('selectContainer') selectContainer: ElementRef;
  @ViewChild('selectInput') selectInput: ElementRef;
  @ViewChild('dropdownSearchInput') dropdownSearchInput: ElementRef<HTMLInputElement>;
  @ViewChild('multiSelectSearchInput') multiSelectSearchInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') auto: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;
  @ViewChild('matMultiSelect') matMultiSelect: MatSelect;
  filteredItems: SelectModel[] = [];
  focused = false;
  panelWidth: number;
  showSearch: boolean;
  touchUpdate = false;
  searchControl: FormControl = new FormControl('');
  searchValue = '';
  isSearchFocused = false;
  private selectionHandled = false;
  private isSelecting = false;
  displayWithFn = (value: any): string => {
    if (!value || !this.listedItems?.length) {return '';}
    const item = this.listedItems.find((i) => i.value === value);
    return this.translate(item?.viewValue);
  };

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private windowService: WindowService,
    private translationService: TranslationService
  ) {
    super();
  }

  ngOnInit() {
    const isSecondaryLayer = localStorage.getItem('isPrimaryLayer') === 'false' && this.windowService.isDesktopRes;
    if (isSecondaryLayer) {
      this.renderer.addClass(this.el.nativeElement, 'secondary-layer');
    }
    this.listedItems = this.items || this.listedItems;
    this.filteredItems = [...this.listedItems];
    if (this.hideSearch) {
      this.showSearch = false;
    } else {
      this.showSearch = this.listedItems?.length > 5;
    }
    if (!this.selectedItem && this.value && this.listedItems?.length > 0) {
      this.selectedItem = this.listedItems.find((item) => this.value === item.value);
    }
    if (this.disableElement && this.form) {
      this.form.disable();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.items) {
      this.listedItems = this.items || this.listedItems;
      this.filteredItems = [...this.listedItems];
      if (!this.hideSearch) {
        this.showSearch = this.listedItems?.length > 5;
      }
      if (!changes.selectedItem || !changes.selectedItem.currentValue) {
        const currentValue = this.value || this.form?.value;
        this.selectedItem = this.listedItems.find((item) => currentValue === item.value);
      }
    }
    if (changes.selectedItem) {
      this.selectedItem = changes.selectedItem.currentValue;
    }
    if (changes.form) {
      const formValue = changes.form.currentValue.value;
      this.form.patchValue(formValue, { emitEvent: false });
      if (this.listedItems?.length > 0) {
        this.selectedItem = this.listedItems.find((item) => item.value === formValue);
      }
    }
    if (changes.disableElement && this.form) {
      if (changes.disableElement.currentValue) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }
  }

  onChange(event: any) {
    this.touchUpdate = true;
    if (this.multiple) {
      this.value = event.value;
      this.form.patchValue(event.value);
      this.optionChange.emit(event.value);
    } else {
      this.optionChange.emit(event.value);
      this.listedItems = this.items || this.listedItems;
      this.form.patchValue(event.value);
      this.selectedItem = this.listedItems.find((item) => event.value === item.value);
    }
  }

  searchItems() {
    try {
      const value = this.searchControl.value;
      this.listedItems = this.items.filter((item) => item.viewValue && item.viewValue.toLowerCase().includes(value.toLowerCase()));
    } catch (_error) { /* ignore filter errors */ }
  }

  setFocused(isOpen: boolean) {
    this.focused = isOpen;
    if (isOpen) {
      this.updatePanelWidth();
    }
  }

  updatePanelWidth() {
    if (this.selectContainer?.nativeElement) {
      this.panelWidth = this.selectContainer.nativeElement.offsetWidth;
    }
  }

  onContainerMouseDown(event: MouseEvent) {
    if (this.disableElement) {
      return;
    }
    event.preventDefault();
    if (this.selectInput?.nativeElement) {
      this.selectInput.nativeElement.focus();
    }
    if (this.autocompleteTrigger) {
      this.autocompleteTrigger.openPanel();
    }
  }

  openPanel() {
    if (this.disableElement) {
      return;
    }
    if (this.selectInput?.nativeElement) {
      this.selectInput.nativeElement.focus();
    }
    if (this.autocompleteTrigger) {
      this.autocompleteTrigger.openPanel();
    }
  }

  openPanelFromLabel() {
    if (this.disableElement) {
      return;
    }
    if (this.multiple) {
      if (this.matMultiSelect && !this.matMultiSelect.panelOpen) {
        this.matMultiSelect.open();
      }
    } else {
      this.openPanel();
    }
  }

  openMultiSelectFromContainer(event: MouseEvent) {
    if (this.disableElement || !this.matMultiSelect) {
      return;
    }
    const target = event.target as Element | null;
    if (target?.closest('.mat-mdc-select, mat-select')) {
      return;
    }
    if (!this.matMultiSelect.panelOpen) {
      this.matMultiSelect.open();
    }
  }

  getDisplayValue(): string {
    if (this.selectedItem) {
      return this.translate(this.selectedItem.viewValue);
    }
    if (this.value && this.listedItems?.length > 0) {
      const item = this.listedItems.find((i) => i.value === this.value);
      return this.translate(item?.viewValue);
    }
    return '';
  }

  // The closed field shows the same text as the open list, which pipes each
  // viewValue through translate. A text with no translation stays as it is.
  private translate(text: string): string {
    if (!text) {
      return '';
    }
    const language = localStorage.getItem('language');
    if (!language || language === 'en') {
      return text;
    }
    return this.translationService.translations?.[text]?.[language] || text;
  }

  onInputFocus() {
    this.focused = true;
    this.updatePanelWidth();
    this.searchValue = '';
    this.filteredItems = [...this.listedItems];
    if (this.showSearch) {
      setTimeout(() => {
        this.dropdownSearchInput?.nativeElement?.focus();
      }, 100);
    }
  }

  onInputBlur() {
    setTimeout(() => {
      if (this.isSearchFocused || this.isSelecting) {
        return;
      }
      this.focused = false;
      this.filteredItems = [...this.listedItems];
      if (this.selectInput?.nativeElement) {
        this.selectInput.nativeElement.value = this.getDisplayValue();
      }
      if (this.autocompleteTrigger) {
        this.autocompleteTrigger.closePanel();
      }
    }, 150);
  }

  onSearchFocus(): void {
    this.isSearchFocused = true;
  }

  onSearchBlur(): void {
    this.isSearchFocused = false;
    setTimeout(() => {
      if (this.isSelecting) {return;}
      const activeElement = document.activeElement;
      if (!this.el.nativeElement.contains(activeElement)) {
        this.focused = false;
        if (this.autocompleteTrigger) {
          this.autocompleteTrigger.closePanel();
        }
      }
    }, 200);
  }

  onSearchInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchValue = input.value.toLowerCase();
    if (!searchValue) {
      this.filteredItems = [...this.listedItems];
    } else {
      this.filteredItems = this.listedItems.filter((item) =>
        item.viewValue && item.viewValue.toLowerCase().includes(searchValue)
      );
    }
  }

  onOptionMouseDown(): void {
    this.isSelecting = true;
    setTimeout(() => this.isSelecting = false, 500);
  }

  onOptionClicked(item: SelectModel): void {
    if (this.multiple || item.value === 'divider' || item.value === '__no_results__') {return;}
    if (this.selectionHandled) {return;}
    this.handleSelection(item.value);
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    this.selectionHandled = true;
    this.handleSelection(event.option.value);
    setTimeout(() => this.selectionHandled = false);
  }

  private handleSelection(selectedValue: any): void {
    this.touchUpdate = true;
    this.value = selectedValue;
    this.form.patchValue(selectedValue);
    this.selectedItem = this.listedItems.find((item) => item.value === selectedValue);
    this.optionChange.emit(selectedValue);
    this.filteredItems = [...this.listedItems];
  }

  isSelected(value: any): boolean {
    return this.value === value || this.form?.value === value || this.selectedItem?.value === value;
  }

  get selectedValue(): any {
    return this.selectedItem?.value ?? this.value ?? this.form?.value;
  }

  onPanelOpened(): void {
    // mat-autocomplete (unlike mat-select) does not scroll the panel to the
    // currently selected option, so it always opens scrolled to the top. Center
    // the selected option in the panel so it is visible and reads as selected.
    // Run after the optional search-input auto-focus (~100ms) so focusing the
    // search box at the top of the panel does not reset the scroll position.
    setTimeout(() => this.scrollToSelectedOption(), 150);
  }

  private scrollToSelectedOption(): void {
    const panel = this.auto?.panel?.nativeElement as HTMLElement | undefined;
    if (!panel) {
      return;
    }
    const selectedEl = panel.querySelector('.tidy-option-selected') as HTMLElement | null;
    if (!selectedEl) {
      return;
    }
    const panelRect = panel.getBoundingClientRect();
    const elRect = selectedEl.getBoundingClientRect();
    const offset = (elRect.top - panelRect.top) + panel.scrollTop;
    panel.scrollTop = offset - (panel.clientHeight / 2) + (selectedEl.clientHeight / 2);
  }

  override writeValue(value: any) {
    super.writeValue(value);
    if (this.listedItems?.length > 0) {
      this.selectedItem = this.listedItems.find((item) => item.value === value);
    }
  }

  onDropdownSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const searchLower = input.value.toLowerCase();
    let newFilteredItems = !searchLower
      ? [...this.listedItems]
      : this.listedItems.filter((item) =>
          item.viewValue?.toLowerCase().includes(searchLower)
        );
    if (newFilteredItems.length === 0) {
      newFilteredItems = [{ value: '__no_results__', viewValue: 'No results found' }];
    }
    this.filteredItems = newFilteredItems;
    this.searchValue = input.value;
    setTimeout(() => {
      if (this.autocompleteTrigger && !this.autocompleteTrigger.panelOpen) {
        this.autocompleteTrigger.openPanel();
      }
      input.focus();
    }, 10);
  }

  clearDropdownSearch(inputElement?: HTMLInputElement): void {
    this.searchValue = '';
    this.filteredItems = [...this.listedItems];
    if (inputElement) {
      inputElement.value = '';
    }
  }

  onMultiSelectOpened(isOpen: boolean): void {
    if (isOpen) {
      this.updatePanelWidth();
      this.searchValue = '';
      this.filteredItems = [...this.listedItems];
      if (this.showSearch) {
        setTimeout(() => {
          this.multiSelectSearchInput?.nativeElement?.focus();
        }, 100);
      }
    } else {
      this.searchValue = '';
      this.filteredItems = [...this.listedItems];
    }
  }

  onMultiSelectSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const searchLower = input.value.toLowerCase();
    this.searchValue = input.value;
    if (!searchLower) {
      this.filteredItems = [...this.listedItems];
    } else {
      const filtered = this.listedItems.filter((item) =>
        item.viewValue?.toLowerCase().includes(searchLower)
      );
      this.filteredItems = filtered.length > 0
        ? filtered
        : [{ value: '__no_results__', viewValue: 'No results found' }];
    }
  }

  clearMultiSelectSearch(): void {
    this.searchValue = '';
    this.filteredItems = [...this.listedItems];
    if (this.multiSelectSearchInput?.nativeElement) {
      this.multiSelectSearchInput.nativeElement.value = '';
      this.multiSelectSearchInput.nativeElement.focus();
    }
  }

  getMultiSelectDisplayValue(): string {
    const selectedValues = this.form?.value;
    if (!selectedValues || !Array.isArray(selectedValues) || selectedValues.length === 0) {
      return this.helperText || '';
    }
    const selectedItems = this.listedItems.filter((item) =>
      selectedValues.includes(item.value)
    );
    if (selectedItems.length === 0) {
      return this.helperText || '';
    }
    return selectedItems.map((item) => this.translate(item.viewValue)).join(', ');
  }

}